import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { SousTraitantService } from '../../../core/services/sous-traitant.service';
import { FormValidators } from '../../../core/validators/form-validators';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-sous-traitant-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MatIconModule, ConfirmDialogComponent],
  templateUrl: `./sous-traitant-edit.component.html`,
  styleUrls: [`./sous-traitant-edit.component.css`]
})
export class SousTraitantEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private stService = inject(SousTraitantService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  stId: number | null = null;
  canDelete = signal(false);
  showDeleteModal = signal(false);
  isArchived = signal(false);

  stForm = this.fb.group({
    codeComptable: ['', [Validators.required]],
    denomination: ['', [Validators.required]],
    siret: ['', [FormValidators.siret()]],
    qualification: [''],
    adresse: [''],
    ville: [''],
    codePostal: ['', [FormValidators.codePostalFr()]]
  });

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.stId = +id;
      this.loadSousTraitant(this.stId);
    }
  }

  loadSousTraitant(id: number) {
    this.isLoading.set(true);
    this.stService.getSousTraitantById(id).subscribe({
      next: (st) => {
        this.stForm.patchValue(st);
        this.isArchived.set(st.archived || false);
        this.isLoading.set(false);
        this.checkDeleteStatus(id);
      },
      error: () => {
        this.errorMessage.set('Erreur lors du chargement du sous-traitant.');
        this.isLoading.set(false);
      }
    });
  }

  checkDeleteStatus(id: number) {
    this.stService.canDeleteSousTraitant(id).subscribe({
      next: (res) => this.canDelete.set(res),
      error: () => this.canDelete.set(false)
    });
  }

  onDeleteClick() {
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    if (this.stId) {
      this.isLoading.set(true);
      this.stService.deleteSousTraitant(this.stId).subscribe({
        next: () => {
          this.router.navigate(['/sous-traitants']);
        },
        error: () => {
          this.errorMessage.set('Erreur lors de la suppression/archivage du sous-traitant.');
          this.isLoading.set(false);
          this.showDeleteModal.set(false);
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
  }

  onSubmit() {
    if (this.stForm.valid && this.stId) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      this.stService.updateSousTraitant(this.stId, this.stForm.value as any).subscribe({
        next: () => {
          this.router.navigate(['/sous-traitants']);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || err?.error || 'Une erreur est survenue lors de la mise à jour.');
          this.isLoading.set(false);
        }
      });
    }
  }
}
